import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { QuestionItemV2 } from '@examdojo/models/question';
import { QuestionEditorState } from '@examdojo/models/question-editor';
import { getFormData } from '@examdojo/question';
import { QuestionImageCacheService } from '@examdojo/question-image';
import isEqual from 'lodash/isEqual';
import { EMPTY, filter, Observable, switchMap, withLatestFrom } from 'rxjs';
import { QuestionItemsStorageService } from '../question-items-storage-service';
import { QuestionPickerDialogService, QuestionPickerResult } from '../ref-question-validation/question-picker';
import { QuestionEditorCachingConfig } from './question-items-caching.types';

@Injectable()
export class QuestionItemsCachingService {
  constructor(
    private readonly questionItemsStorageService: QuestionItemsStorageService,
    private readonly questionPickerDialogService: QuestionPickerDialogService,
    private readonly questionImageCacheService: QuestionImageCacheService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  /**
   * Loads question items with caching logic.
   * Handles cached vs fresh data selection via user dialog.
   */
  async loadQuestionItemsWithCaching(config: QuestionEditorCachingConfig): Promise<QuestionItemV2[] | null> {
    this.questionItemsStorageService.disableSaving('load_question_items_with_caching');
    const mode = config.mode;
    assertNonNullable(mode, 'mode');

    const cachedQuestionData = await this.questionItemsStorageService.getQuestionState({
      questionType: config.questionType,
      activeMode: mode,
      activeQuestionId: config.questionId,
    });

    const freshQuestionItems = config.freshQuestionItems;
    if (!freshQuestionItems) {
      this.questionItemsStorageService.enableSaving('load_question_items_with_caching');
      return null;
    }

    const freshQuestionState: QuestionEditorState = {
      questionItems: freshQuestionItems,
      imageCache: {},
      timestamp: Date.now(),
    };

    const processedCachedQuestionData = cachedQuestionData ? getFormData(cachedQuestionData.questionItems) : null;
    const processedFreshQuestionState = getFormData(freshQuestionState.questionItems);

    if (!cachedQuestionData || isEqual(processedCachedQuestionData, processedFreshQuestionState)) {
      this.questionItemsStorageService.enableSaving('load_question_items_with_caching');
      return freshQuestionState.questionItems;
    }

    const result = await this.questionPickerDialogService.openDialog({
      input: {
        cachedQuestionState: cachedQuestionData,
        newQuestionState: freshQuestionState,
        markCalculationMethod: config.markCalculationMethod,
      },
    });

    this.questionItemsStorageService.enableSaving('load_question_items_with_caching');

    if (result === QuestionPickerResult.UseCached) {
      this.questionImageCacheService.loadFromCache(cachedQuestionData.imageCache);
      return cachedQuestionData.questionItems;
    } else {
      return freshQuestionState.questionItems;
    }
  }

  /**
   * Sets up auto-saving when form changes.
   */
  setupAutoSave(
    config$: Observable<QuestionEditorCachingConfig | null>,
    currentState$: Observable<QuestionItemV2[]>,
  ): Observable<void> {
    return currentState$.pipe(
      filter(isNotNullish),
      withLatestFrom(config$),
      switchMap(async ([questionItems, config]) => {
        if (!config) {
          return;
        }

        const imageCache = await this.questionImageCacheService.getCache();
        const mode = config.mode;
        const activeId = config.questionId;

        if (mode && isNotNullish(activeId)) {
          await this.questionItemsStorageService.saveQuestionState(
            { questionItems, imageCache, timestamp: Date.now() },
            {
              questionType: config.questionType,
              activeMode: mode,
              activeQuestionId: activeId,
            },
          );
        }
      }),
      this.errorHandlerService.catchError(`[QuestionItemsCachingService]: failed to save question state`, () => EMPTY, {
        toast: {
          title: 'Failed to save question state',
        },
      }),
    );
  }

  handleLoadError(error: unknown, fallbackFn?: () => QuestionItemV2[] | null): QuestionItemV2[] | null {
    this.errorHandlerService.error(`[QuestionItemsCachingService]: failed to load question items`, {
      err: error,
      toast: {
        title: 'Failed to load question items',
      },
    });

    return fallbackFn ? fallbackFn() : null;
  }
}
